import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Portal, Stack, useTheme } from '@mui/material';

const HOVER_OPEN_MS = 450;

export function DealRoomPropertyThumbnails({
  urls,
  addressLabel,
}: {
  urls: string[];
  addressLabel: string;
}) {
  const theme = useTheme();
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearHoverTimer = useCallback(() => {
    if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
  }, []);

  const openAt = useCallback(
    (index: number) => {
      clearHoverTimer();
      setOpenIndex(index);
    },
    [clearHoverTimer]
  );

  const close = useCallback(() => {
    clearHoverTimer();
    setOpenIndex(null);
  }, [clearHoverTimer]);

  const scheduleOpen = useCallback(
    (index: number) => {
      clearHoverTimer();
      hoverTimerRef.current = setTimeout(() => openAt(index), HOVER_OPEN_MS);
    },
    [clearHoverTimer, openAt]
  );

  useEffect(() => {
    if (openIndex === null) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [openIndex, close]);

  useEffect(() => () => clearHoverTimer(), [clearHoverTimer]);

  if (!urls.length) return null;

  const isOpen = openIndex !== null;
  const activeSrc = isOpen ? urls[openIndex] : null;

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        component="span"
        aria-label="Listing photos"
        onMouseLeave={clearHoverTimer}
        sx={{
          flexShrink: 0,
          position: 'relative',
          py: 0.25,
          '& > *:not(:first-of-type)': { ml: -1 },
        }}
      >
        {urls.map((src, i) => (
          <Box
            key={`${src}-${i}`}
            component="img"
            src={src}
            alt={i === 0 ? `${addressLabel || 'Property'} — listing photo` : ''}
            loading="lazy"
            decoding="async"
            role="button"
            tabIndex={0}
            onClick={() => openAt(i)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                openAt(i);
              }
            }}
            onMouseEnter={() => scheduleOpen(i)}
            onMouseLeave={clearHoverTimer}
            sx={{
              width: 36,
              height: 36,
              objectFit: 'cover',
              borderRadius: 1,
              border: `2px solid ${theme.palette.background.paper}`,
              boxShadow: theme.shadows[2],
              flexShrink: 0,
              position: 'relative',
              cursor: 'pointer',
              transition: 'box-shadow 0.2s ease-out, opacity 0.2s ease-out',
              '&:hover': {
                zIndex: 4,
                boxShadow: theme.shadows[6],
                opacity: 0.92,
              },
            }}
          />
        ))}
      </Stack>

      {isOpen && activeSrc ? (
        <Portal>
          <Box
            role="dialog"
            aria-modal="true"
            aria-label={`${addressLabel || 'Property'} listing photo`}
            onClick={close}
            sx={{
              position: 'fixed',
              inset: 0,
              zIndex: theme.zIndex.modal + 2,
              bgcolor: 'rgba(0, 0, 0, 0.94)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: { xs: 2, sm: 4 },
              cursor: 'zoom-out',
            }}
          >
            <Box
              component="img"
              src={activeSrc}
              alt={addressLabel || 'Property listing photo'}
              sx={{
                maxWidth: '100%',
                maxHeight: '100%',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
                userSelect: 'none',
                pointerEvents: 'none',
              }}
            />
          </Box>
        </Portal>
      ) : null}
    </>
  );
}

export function galleryUrlsForProperty(property: {
  galleryImageUrls: string[];
  imageUrl?: string | null;
}): string[] {
  if (property.galleryImageUrls.length > 0) return property.galleryImageUrls;
  return property.imageUrl ? [property.imageUrl] : [];
}
